import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

const FALLBACK = [
  {
    id: 1,
    name: "Richard Harrison",
    role: "Personal Trainer",
    rating: 5,
    message: "Hybrid Performance Academy completely transformed my training approach. The structured video libraries and expert coaching have taken my performance to a new level.",
  },
  {
    id: 2,
    name: "Sarah Mitchell",
    role: "Fitness Enthusiast",
    rating: 5,
    message: "The platform makes it so easy to follow structured programs. I've seen incredible results in just 8 weeks following the strength-focused library.",
  },
  {
    id: 3,
    name: "Omar Khalil",
    role: "Athlete",
    rating: 5,
    message: "Best investment I've made in my fitness journey. The content quality and coaching expertise is unmatched.",
  },
];

const StarRating = ({ rating }) => (
  <ul className="testimonial-rating justify-content-start wow fadeInUp">
    {[1, 2, 3, 4, 5].map((n) => (
      <li key={n}>
        <i className={`fa-${n <= rating ? "solid" : "regular"} fa-star`} style={{ color: n <= rating ? "#f5a623" : "#555" }} />
      </li>
    ))}
  </ul>
);

const Testimonial = () => {
  const swiperRef = useRef(null);
  const [items, setItems] = useState(FALLBACK);
  const { user } = useAuth();
  const { t } = useLanguage();
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    apiClient.get("/homepage/testimonials")
      .then(({ data }) => { if (data && data.length > 0) setItems(data); })
      .catch(() => {});
  }, []);

  const submitFeedback = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");

    try {
      const { data } = await apiClient.post("/testimonials", { message, rating });
      setItems((current) => [
        data,
        ...current.filter((item) => item.id !== data.id),
      ]);
      setMessage("");
      setStatus(t("Thank you. Your feedback is now displayed."));
      window.setTimeout(() => swiperRef.current?.swiper?.slideTo(0), 0);
    } catch (error) {
      const validationMessage = error.response?.data?.errors?.message?.[0];
      setStatus(validationMessage || t("Your feedback could not be submitted. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="swiper swiper-fade testimonial-swiper swiper-btn-lr">
        <Swiper
          className="swiper-wrapper"
          loop={true}
          speed={1300}
          ref={swiperRef}
        >
          {items.map((item, ind) => (
            <SwiperSlide className="swiper-slide" key={item.id || ind}>
              <div className="testimonial-1 testimonial-left testimonial-no-photo">
                <div className="testimonial-info wow fadeInUp">
                  <StarRating rating={item.rating || 5} />
                  <p className="testimonial-text">{item.message}</p>
                  <h4 className="testimonial-name">{item.name}</h4>
                  <span className="testimonial-position text-primary">{item.role}</span>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          onClick={() => swiperRef.current.swiper.slidePrev()}
          className="testimonial-button-prev btn-prev"
        >
          <i className="fa-solid fa-arrow-left"></i>
        </div>
        <div
          onClick={() => swiperRef.current.swiper.slideNext()}
          className="testimonial-button-next btn-next"
        >
          <i className="fa-solid fa-arrow-right"></i>
        </div>
      </div>

      {user && (
        <form className="hpa-testimonial-form" onSubmit={submitFeedback}>
          <div className="hpa-testimonial-form-heading">
            <span>{t("Share your experience")}</span>
            <h3>{t("Leave feedback as {name}", { name: user.name })}</h3>
            <p>{t("Your account name will be shown automatically with your feedback.")}</p>
          </div>
          <div className="hpa-testimonial-fields">
            <label>
              {t("Rating")}
              <select
                value={rating}
                onChange={(event) => setRating(Number(event.target.value))}
                disabled={submitting}
              >
                {[5, 4, 3, 2, 1].map((value) => (
                  <option key={value} value={value}>{t("{count} stars", { count: value })}</option>
                ))}
              </select>
            </label>
            <label>
              {t("Your feedback")}
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                minLength={10}
                maxLength={1000}
                rows={5}
                placeholder={t("Tell others about your experience with the academy...")}
                required
                disabled={submitting}
              />
            </label>
            <button type="submit" className="btn btn-primary btn-skew" disabled={submitting}>
              <span>{submitting ? t("Publishing...") : t("Publish Feedback")}</span>
            </button>
            {status && <p className="hpa-testimonial-status" role="status">{status}</p>}
          </div>
        </form>
      )}

    </>
  );
};

export default Testimonial;
